import { AxiosError, isAxiosError } from "axios";
import { AuthErrorBody, AuthErrorCode } from "../types";

const KNOWN_CODES: AuthErrorCode[] = [
  "VALIDATION_ERROR",
  "INVALID_SESSION",
  "SESSION_CLOSED",
  "EXPIRED",
  "CHANNEL_UNAVAILABLE",
  "REQUEST_CONFLICT",
  "SEND_COOLDOWN",
  "INVALID_CODE",
  "RATE_LIMIT_EXCEEDED",
];

/**
 * Ошибка OTP v3. Создаётся и для HTTP-ошибок, и для HTTP 200 с success: false.
 */
export class AuthError extends Error {
  code: AuthErrorCode;
  status?: number;
  description?: string;
  nextSendAt?: string;
  /** Пауза из заголовка Retry-After, мс. */
  retryAfterMs?: number;

  constructor(params: {
    code: AuthErrorCode;
    status?: number;
    description?: string;
    nextSendAt?: string;
    retryAfterMs?: number;
  }) {
    super(params.description || params.code);
    this.name = "AuthError";
    this.code = params.code;
    this.status = params.status;
    this.description = params.description;
    this.nextSendAt = params.nextSendAt;
    this.retryAfterMs = params.retryAfterMs;
  }
}

const parseRetryAfter = (value: unknown): number | undefined => {
  if (typeof value !== "string" && typeof value !== "number") {
    return undefined;
  }

  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const date = Date.parse(String(value));
  return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
};

export const fromErrorBody = (
  body: AuthErrorBody | undefined,
  status?: number,
  retryAfter?: unknown,
): AuthError => {
  const rawCode = body?.error?.code as AuthErrorCode | undefined;
  let code: AuthErrorCode =
    rawCode && KNOWN_CODES.includes(rawCode) ? rawCode : "UNKNOWN";

  if (code === "UNKNOWN" && status === 429) {
    code = "RATE_LIMIT_EXCEEDED";
  }

  return new AuthError({
    code,
    status,
    description: body?.error?.description || body?.message,
    nextSendAt: body?.next_send_at,
    retryAfterMs: parseRetryAfter(retryAfter),
  });
};

/** Приводит любую ошибку запроса к AuthError. */
export const toAuthError = (error: unknown): AuthError => {
  if (error instanceof AuthError) {
    return error;
  }

  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<AuthErrorBody>;

    if (!axiosError.response) {
      return new AuthError({ code: "NETWORK" });
    }

    const { data, status, headers } = axiosError.response;
    return fromErrorBody(
      data && typeof data === "object" ? data : undefined,
      status,
      headers?.["retry-after"],
    );
  }

  return new AuthError({ code: "UNKNOWN" });
};
